import axios from 'axios'

/**
 * ── A DELIBERATE INVERSION OF THE PRODUCT'S BEHAVIOUR ───────────────────────
 *
 * The product SPA's parseApiError is ~380 lines, and most of that is redacting
 * internal jargon: its reader is a CUSTOMER, so a Postgres error code or a
 * service name in a toast is noise at best and a leak at worst.
 *
 * Here the reader is the person who will fix it. This app is unreachable
 * without a live staff row, so the server sends its real message (see
 * crates/api-core/src/error.rs, which documents the same inversion on its side),
 * and we show it VERBATIM — on every failure, not only 5xx — next to the trace
 * id. Redacting here would mean a staff member pasting "Something went wrong"
 * into Slack, which is the opposite of what an internal console is for.
 */
export interface ParsedApiError {
  /** The server's own words. Shown as-is. */
  message: string
  /** Stable, switchable classification. Never render this; branch on it. */
  kind: string
  status?: number
  /** W3C traceparent, greppable against CloudWatch Logs Insights. */
  traceId?: string
  details?: unknown
}

const GENERIC = 'La petición falló y el servidor no dijo por qué.'

export function parseApiError(error: unknown): ParsedApiError {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    const body = error.response?.data as
      | { message?: string; kind?: string; details?: unknown }
      | undefined
    const traceparent =
      (error.response?.headers?.['traceparent'] as string | undefined) ??
      (error.config?.headers?.['traceparent'] as string | undefined)

    if (!error.response) {
      return {
        message:
          error.code === 'ECONNABORTED'
            ? 'La petición superó el tiempo límite del cliente (35 s). Con statement_timeout en 8 s y REQUEST_TIMEOUT en 25 s, esto no es lentitud: es una petición perdida.'
            : 'No se pudo contactar al servicio. ¿Está corriendo `make dev`? ¿Sigue abierto el túnel? Una sesión SSM caduca sola a los ~20 minutos de inactividad.',
        kind: 'network',
        traceId: traceId(traceparent),
      }
    }

    return {
      message: body?.message?.trim() || GENERIC,
      kind: body?.kind ?? 'unknown',
      status,
      traceId: traceId(traceparent),
      details: body?.details,
    }
  }
  return { message: error instanceof Error ? error.message : GENERIC, kind: 'client' }
}

/** The 32-hex trace id out of a W3C traceparent (`00-<trace>-<span>-<flags>`). */
function traceId(traceparent?: string): string | undefined {
  const parts = traceparent?.split('-')
  return parts && parts.length >= 3 ? parts[1] : undefined
}

/** Short, quotable form for a Slack message. */
export function incidentCode(traceId?: string): string {
  return traceId ? traceId.slice(0, 8) : '—'
}
